import React, { useContext, useState, useEffect } from "react";
import {
    TableHead,
    TableRow,
    TableCell,
    TableSortLabel,
    IconButton,
    TextField,
    Box,
} from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { CommonContext } from "../../context/CommonContext";
import { ProductsContext } from "../../context/ProductsContext";

const baseHeadCells = [
    { id: "id", label: "ID" },
    { id: "name", label: "Product Name" },
    { id: "sku_code", label: "SKU Code" },
    { id: "brand", label: "Brand" },
    { id: "category", label: "Category" },
    { id: "subcategory", label: "Subcategory" },
    { id: "invoice_value", label: "Item Value" },
    { id: "gst_percentage", label: "GST %" },
    { id: "created_at", label: "Created At" },
    { id: "status", label: "Status" },
    { id: "action", label: "Action" },
];

// columns that have filters
const filterableColumns = ["id", "name", "sku_code", "brand", "category", "subcategory"];

export default function ProductsTableHead({
    order,
    orderBy,
    onRequestSort,
}) {
    const { allData, setAllProducts } = useContext(ProductsContext);
    const { user } = useContext(CommonContext);

    const headCells = user?.user_type !== 'Merchant'
        ? [
            baseHeadCells[0],
            { id: "merchant", label: "Merchant" },
            ...baseHeadCells.slice(1)
        ]
        : baseHeadCells;

    const [filters, setFilters] = useState({});
    const [showFilters, setShowFilters] = useState({});

    // handle sort click
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    // handle input change
    const handleFilterChange = (e, key) => {
        const { value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // toggle filter field visibility
    const toggleFilter = (key) => {
        setShowFilters((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    // apply filters
    useEffect(() => {
        if (!allData?.data) return;
        let filteredData = [...allData.data];

        Object.entries(filters).forEach(([key, value]) => {
            if (value && value.trim() !== "") {
                filteredData = filteredData.filter((item) => {
                    let cellValue = "";
                    if (key === 'brand') {
                        cellValue = item.brand?.name?.toString().toLowerCase() || "";
                    } else if (key === 'category') {
                        cellValue = item.category?.name?.toString().toLowerCase() || "";
                    } else if (key === 'subcategory') {
                        cellValue = item.subcategory?.name?.toString().toLowerCase() || "";
                    } else {
                        cellValue = item[key]?.toString().toLowerCase() || "";
                    }
                    return cellValue.includes(value.toLowerCase());
                });
            }
        });

        setAllProducts(filteredData);
    }, [filters, allData, setAllProducts]);

    const stickyActionStyle = {
        position: 'sticky',
        right: 0,
        backgroundColor: '#fff',
        borderLeft: '1px solid #ddd',
        boxShadow: 'inset 4px 0 8px -4px rgba(0, 0, 0, 0.2)', // subtle shadow on the left
        zIndex: 3,
    };

    return (
        <TableHead>
            {/* Header Row */}
            <TableRow>
                {headCells.map((headCell) => {
                    const isFilterable = filterableColumns.includes(headCell.id);
                    const isAction = headCell.id === "action";

                    return (
                        <TableCell
                            key={headCell.id}
                            sortDirection={orderBy === headCell.id ? order : false}
                            sx={isAction ? stickyActionStyle : { whiteSpace: 'nowrap' }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <TableSortLabel
                                    active={orderBy === headCell.id}
                                    direction={orderBy === headCell.id ? order : "asc"}
                                    onClick={createSortHandler(headCell.id)}
                                >
                                    {headCell.label}
                                </TableSortLabel>

                                {isFilterable && (
                                    <IconButton
                                        size="small"
                                        onClick={() => toggleFilter(headCell.id)}
                                        sx={{
                                            ml: 1,
                                            color: showFilters[headCell.id] ? "primary.main" : "inherit",
                                        }}
                                    >
                                        <FilterList fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </TableCell>
                    );
                })}
            </TableRow>

            {/* Filter Row (only visible for selected columns) */}
            {Object.values(showFilters).some(Boolean) && (
                <TableRow>
                    {headCells.map((headCell) => {
                        const showFilter =
                            filterableColumns.includes(headCell.id) && showFilters[headCell.id];
                        const isAction = headCell.id === "action";

                        return (
                            <TableCell
                                key={`${headCell.id}-filter`}
                                sx={{
                                    ...(isAction && stickyActionStyle),
                                    paddingTop: 0,
                                    paddingBottom: 0,
                                }}
                            >
                                {showFilter ? (
                                    <TextField
                                        variant="standard"
                                        placeholder={`Search By ${headCell.label}`}
                                        value={filters[headCell.id] || ""}
                                        onChange={(e) => handleFilterChange(e, headCell.id)}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            fontSize: '0.65rem',
                                            '& .MuiInputBase-input': {
                                                fontSize: '0.75rem',
                                                padding: '3px 0 3px 8px',
                                            }
                                        }}
                                    />
                                ) : null}
                            </TableCell>
                        );
                    })}
                </TableRow>
            )}
        </TableHead>
    );
}
